import React from 'react';
import set from 'lodash.set';
import { experimental } from '@trp-beacon-ui-kit/react-theme/warnings';
import PropTypes from 'prop-types';

export const FormContext = React.createContext();

// we are using sparse arrays through a little
// javascript trickery for array fields
// where the name of the field  contains an ever auto-incrementing
// whole number we then use lodash.set to construct a nested
// object with array fields
// rather than the usual flat key=>value (field name => field value)
// structure of form data.
// so if the form names flat data is
// person[0].firstName = 'charlie';
// person[0].lastName = 'hoover';
// person[3].firstName = 'john';
// person[3].lastName = 'smith';
// ----
// then the object passed to this condese transformation would be:
//
// { person : [
//    { firstName : 'charlie', 'lastName' : 'hoover },
//     empty x 2,
//    { firstName : 'john', 'lastName' : 'smith },
// ]}
//
// and the object passed to the onSubmit handler would be
//
// { person : [
//    { firstName : 'charlie', 'lastName' : 'hoover },
//    { firstName : 'john', 'lastName' : 'smith }
// ]}
//
// notice how the above field is without the skipped indexes.
// this should work for nested sparse arrays.
const compactNestedSparseArrays = (obj) => {
  if (typeof obj !== 'object') return obj;

  return Array.isArray(obj)
    ? obj.filter((cur) => ![null, undefined].includes(cur)).map(compactNestedSparseArrays)
    : Object.keys(obj).reduce(
        (newObj, key) => ({
          ...newObj,
          [key]: compactNestedSparseArrays(obj[key]),
        }),
        {},
      );
};

const transformBeforeSubmit = (obj) => {
  const transformed = Object.keys(obj).reduce((accumulator, key) => set(accumulator, key, obj[key]), {});

  return compactNestedSparseArrays(transformed);
};

const Form = ({ children, onSubmit, domSubmitEnabled }) => {
  const submitHooks = new Set();

  const errors = {};
  const values = {};

  const submit = (...args) => {
    submitHooks.forEach((fn) => fn());
    const submitArgs = [Object.keys(errors).length ? errors : null, transformBeforeSubmit(values), ...args];
    onSubmit(...submitArgs);
    return submitArgs;
  };
  const registerSubmitHook = (fn) => submitHooks.add(fn);
  const unregisterSubmitHook = (fn) => submitHooks.delete(fn);

  const registerInput = (name, err, val) => {
    if (err) {
      errors[name] = err;
    } else if (errors[name]) delete errors[name];

    if (val) {
      values[name] = val;
    } else if (values[name]) delete values[name];
  };

  const removeField = (name) => {
    delete errors[name];
    delete values[name];
  };

  const removeFieldMatch = (name) => {
    Object.keys(errors).forEach((key) => key.startsWith(name) && delete errors[key]);
    Object.keys(values).forEach((key) => key.startsWith(name) && delete values[key]);
  };
  const unwrapped = (
    <FormContext.Provider
      value={{
        registerSubmitHook,
        unregisterSubmitHook,
        registerInput,
        removeField,
        // useful for removing nested fields too...
        removeFieldMatch,
        values,
        submit,
      }}>
      {typeof children === 'function' ? children(submit) : children}
    </FormContext.Provider>
  );

  return domSubmitEnabled ? <form onSubmit={submit}>{unwrapped}</form> : unwrapped;
};

Form.propTypes = {
  /**
   * The Form contents, accepts either a render prop or node, and
   * provides a submit function as the first param of the render prop
   * also provides a submit function from a FormContext which wraps the children.
   * a call to `submit` function triggers validation on all <InputField> components and
   * extensions (e.g. CheckboxField, SelectField). When a call to submit is made
   * validations are preformed and the onSubmit callback is called.
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /**
   * a callback function which is called upon invocation of the `submit` function passed to its children
   *
   * callback has the signature (errors, values) => {...}
   * where the first argument is an object of errors {[fieldName] : errorMsg}
   * if no errors exist the first argument will be `null`
   * and the second argument is the values of each InputField. {[fieldNames]: value }
   *
   * NOTE: `submit` function returns an array where the first element is an object of
   * errors {[fieldName] : errorMsg}
   * if no errors exist the first element will be `null`.
   * The second element contains the values of each InputField. {[fieldNames]: value }
   */
  onSubmit: PropTypes.func.isRequired,
  /**
   * a boolean option that determines whether the form component is wrapped in an actual HTML form
   * this is needed for the form to automatically submit on a DOM 'submit' event
   * (triggered when return carriage pressed on focused field, or submit type button is pressed)
   */
  domSubmitEnabled: PropTypes.bool.isRequired,
};

export default experimental(Form);
